import type { LatLngTuple } from '@groupe-reca/contracts'
import { fromLocal, toLocal, type LatLng, type LocalPoint } from '@/features/detection/domain/geometry'

// Parcours prévu en mètres locaux : sommets enchaînés, distance cumulée, étape de chaque segment. Projection d'un
// point sur une fenêtre de distance (jamais sur tout le parcours d'un coup : boucles, rues parcourues deux fois et voies
// parallèles se distinguent par leur position le long du parcours, pas seulement dans l'espace).

export interface RoutePolyline {
  origin: LatLng
  points: LocalPoint[]
  /** Distance cumulée (m) à chaque sommet. */
  along: number[]
  /** Étape (index dans `legs`) de chaque segment `i → i + 1`. */
  segmentLeg: number[]
  /** Distance cumulée à la fin de chaque étape (arrivée à la résidence `legs[i].to`). */
  legEnd: number[]
  length: number
}

export interface Projection {
  along: number
  /** Écart perpendiculaire (m). */
  distance: number
  segment: number
  point: LocalPoint
}

export function buildPolyline(legs: readonly (readonly LatLngTuple[])[]): RoutePolyline | null {
  const first = legs[0]?.[0]
  if (!first) return null
  const origin = { lat: first[0], lng: first[1] }
  const points: LocalPoint[] = []
  const along: number[] = []
  const segmentLeg: number[] = []
  const legEnd: number[] = []
  legs.forEach((leg, legIndex) => {
    for (const [lat, lng] of leg) {
      const point = toLocal(origin, { lat, lng })
      const last = points[points.length - 1]
      if (last && Math.hypot(point.x - last.x, point.y - last.y) < 0.01) continue
      if (last) segmentLeg.push(legIndex)
      along.push(last ? (along[along.length - 1] ?? 0) + Math.hypot(point.x - last.x, point.y - last.y) : 0)
      points.push(point)
    }
    legEnd.push(along[along.length - 1] ?? 0)
  })
  if (points.length < 2) return null
  return { origin, points, along, segmentLeg, legEnd, length: along[along.length - 1] ?? 0 }
}

export function localOf(polyline: RoutePolyline, point: LatLng): LocalPoint {
  return toLocal(polyline.origin, point)
}

export function latLngOf(polyline: RoutePolyline, point: LocalPoint): LatLng {
  return fromLocal(polyline.origin, point)
}

/** Point du parcours à une distance donnée (bornée au parcours). */
export function pointAt(polyline: RoutePolyline, distance: number): LocalPoint {
  const target = Math.max(0, Math.min(polyline.length, distance))
  for (let index = 1; index < polyline.points.length; index += 1) {
    const end = polyline.along[index] ?? 0
    if (end >= target) {
      const start = polyline.along[index - 1] ?? 0
      const a = polyline.points[index - 1]
      const b = polyline.points[index]
      if (!a || !b) break
      const t = end === start ? 0 : (target - start) / (end - start)
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t }
    }
  }
  return polyline.points[polyline.points.length - 1] ?? { x: 0, y: 0 }
}

/** Cap (degrés, 0 = nord) d'un point local vers un autre. */
export function bearingOf(from: LocalPoint, to: LocalPoint): number {
  return ((Math.atan2(to.x - from.x, to.y - from.y) * 180) / Math.PI + 360) % 360
}

/**
 * Orientation du tronçon prévu : direction moyenne sur une fenêtre (`behind` m derrière, `ahead` m devant). Une courbe
 * tourne donc progressivement et un coude d'intersection ne bascule qu'en le franchissant.
 */
export function bearingAt(polyline: RoutePolyline, distance: number, ahead = 25, behind = 5): number {
  const from = pointAt(polyline, distance - behind)
  const to = pointAt(polyline, Math.min(polyline.length, distance + ahead))
  if (Math.hypot(to.x - from.x, to.y - from.y) < 0.5) {
    return bearingOf(pointAt(polyline, distance - ahead), pointAt(polyline, distance))
  }
  return bearingOf(from, to)
}

/** Projection la plus proche dans la fenêtre `[fromAlong, toAlong]`; `null` si la fenêtre est vide. */
export function project(polyline: RoutePolyline, point: LocalPoint, fromAlong = 0, toAlong = Infinity): Projection | null {
  let best: Projection | null = null
  for (let index = 0; index < polyline.points.length - 1; index += 1) {
    const startAlong = polyline.along[index] ?? 0
    const endAlong = polyline.along[index + 1] ?? 0
    if (endAlong < fromAlong || startAlong > toAlong) continue
    const a = polyline.points[index]
    const b = polyline.points[index + 1]
    if (!a || !b) continue
    const dx = b.x - a.x
    const dy = b.y - a.y
    const lengthSquared = dx * dx + dy * dy
    let t = lengthSquared === 0 ? 0 : ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared
    t = Math.max(0, Math.min(1, t))
    let along = startAlong + (endAlong - startAlong) * t
    if (along < fromAlong || along > toAlong) {
      along = Math.max(fromAlong, Math.min(toAlong, along))
      t = endAlong === startAlong ? 0 : (along - startAlong) / (endAlong - startAlong)
    }
    const projected = { x: a.x + dx * t, y: a.y + dy * t }
    const distance = Math.hypot(point.x - projected.x, point.y - projected.y)
    if (!best || distance < best.distance) best = { along, distance, segment: index, point: projected }
  }
  return best
}

/** Découpe le parcours à une distance : partie parcourue et partie restante (sommets lat/lng). */
export function splitAt(polyline: RoutePolyline, distance: number): { travelled: LatLng[]; remaining: LatLng[] } {
  const cut = Math.max(0, Math.min(polyline.length, distance))
  const travelled: LatLng[] = []
  const remaining: LatLng[] = []
  polyline.points.forEach((point, index) => {
    const along = polyline.along[index] ?? 0
    if (along <= cut) travelled.push(latLngOf(polyline, point))
    if (along >= cut) remaining.push(latLngOf(polyline, point))
  })
  const middle = latLngOf(polyline, pointAt(polyline, cut))
  if (cut > 0 && cut < polyline.length) {
    travelled.push(middle)
    remaining.unshift(middle)
  }
  return { travelled: cut > 0 ? travelled : [], remaining }
}
